import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, HelpCircleIcon, Check, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { useTasks } from '../../contexts/TaskContext';
export interface NavActionsProps {
  'data-id'?: string;
}
interface Notification {
  id: string;
  category: 'vip' | 'loyalty' | 'engagement' | 'system' | 'task';
  message: string;
  time: string;
  unread: boolean;
  taskPriority?: 'Low' | 'Medium' | 'High' | 'Urgent';
  taskType?: 'assigned' | 'due' | 'overdue' | 'completed';
  linkTo?: string;
}
const sampleNotifications: Notification[] = [
// VIP Alerts
{
  id: '1',
  category: 'vip',
  message: 'VIP Alert: Michael Torres skipped their December box',
  time: '5m ago',
  unread: true
}, {
  id: '2',
  category: 'vip',
  message: 'VIP Alert: Amanda Chen cancelled - Reason: Moving overseas',
  time: '12m ago',
  unread: true
}, {
  id: '3',
  category: 'vip',
  message: 'Top subscriber Jennifer Walsh swapped 3 products',
  time: '1h ago',
  unread: false
}, {
  id: '4',
  category: 'vip',
  message: 'High-value: Robert Kim paused - 18 month subscriber',
  time: '2h ago',
  unread: false
}, {
  id: '5',
  category: 'vip',
  message: 'VIP payment failed: Diana Patel ($289/month)',
  time: '3h ago',
  unread: true
},
// Loyalty & Rewards
{
  id: '6',
  category: 'loyalty',
  message: 'Sarah Mitchell unlocked Gold tier - 500 points reached',
  time: '4h ago',
  unread: false
}, {
  id: '7',
  category: 'loyalty',
  message: 'James Cooper redeemed reward: Free Shipping for Life',
  time: '5h ago',
  unread: false
}, {
  id: '8',
  category: 'loyalty',
  message: 'Emily Park is 20 points from Platinum status',
  time: '6h ago',
  unread: false
}, {
  id: '9',
  category: 'loyalty',
  message: 'Marcus Webb earned 50 bonus points - 6 month milestone',
  time: '8h ago',
  unread: false
}, {
  id: '10',
  category: 'loyalty',
  message: 'New reward redemption: Lisa Chen claimed Free Wine Tote',
  time: '1d ago',
  unread: false
},
// Engagement Signals
{
  id: '11',
  category: 'engagement',
  message: 'Win-back opportunity: David Kim logged in after 3 months inactive',
  time: '1d ago',
  unread: false
}, {
  id: '12',
  category: 'engagement',
  message: 'High engagement: Anna Wilson browsing upgrade options',
  time: '2d ago',
  unread: false
}, {
  id: '13',
  category: 'engagement',
  message: 'At-risk VIP: No portal login in 60 days - Jennifer Adams',
  time: '2d ago',
  unread: false
},
// System/Admin
{
  id: '14',
  category: 'system',
  message: 'Scheduled maintenance: Sync paused 2am-4am AEST',
  time: '3d ago',
  unread: false
}, {
  id: '15',
  category: 'system',
  message: 'API rate limit warning - 80% capacity reached',
  time: '3d ago',
  unread: false
}, {
  id: '16',
  category: 'system',
  message: 'New Shopify app update available',
  time: '4d ago',
  unread: false
}];
type FilterCategory = 'all' | 'vip' | 'loyalty' | 'engagement' | 'system' | 'task';
const filterLabels: Record<FilterCategory, string> = {
  all: 'All',
  task: 'Tasks',
  vip: 'VIP',
  loyalty: 'Loyalty',
  engagement: 'Engagement',
  system: 'System'
};
const categoryLabels: Record<string, string> = {
  task: 'Tasks',
  vip: 'VIP Alerts',
  loyalty: 'Loyalty & Rewards',
  engagement: 'Engagement Signals',
  system: 'System & Admin'
};
const taskPriorityDotColor: Record<string, string> = {
  Urgent: '#EF4444',
  High: '#F97316',
  Medium: '#F59E0B',
  Low: '#10B981'
};
export function NavActions({
  'data-id': dataId
}: NavActionsProps) {
  const navigate = useNavigate();
  const {
    getTaskNotifications
  } = useTasks();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  // Merge sample notifications with task notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);
  useEffect(() => {
    const taskNotifs = getTaskNotifications('Sarah Chen');
    setNotifications([...taskNotifs, ...sampleNotifications]);
  }, [getTaskNotifications]);
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const filteredNotifications = activeFilter === 'all' ? notifications : notifications.filter(n => n.category === activeFilter);
  const unreadCount = notifications.filter(n => n.unread).length;
  const filteredUnreadCount = filteredNotifications.filter(n => n.unread).length;
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && buttonRef.current && !dropdownRef.current.contains(event.target as Node) && !buttonRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    if (isNotificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationsOpen]);
  const handleMarkAllRead = () => {
    if (activeFilter === 'all') {
      setNotifications(notifications.map(n => ({
        ...n,
        unread: false
      })));
    } else {
      setNotifications(notifications.map(n => n.category === activeFilter ? {
        ...n,
        unread: false
      } : n));
    }
  };
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    setNotifications(prev => prev.map(n => n.id === notification.id ? {
      ...n,
      unread: false
    } : n));
    // Navigate if link exists
    if (notification.linkTo) {
      setIsNotificationsOpen(false);
      navigate(notification.linkTo);
    }
  };
  const groupedNotifications = filteredNotifications.reduce((acc, notification) => {
    if (!acc[notification.category]) {
      acc[notification.category] = [];
    }
    acc[notification.category].push(notification);
    return acc;
  }, {} as Record<string, Notification[]>);
  // Order categories so 'task' appears first
  const categoryOrder = ['task', 'vip', 'loyalty', 'engagement', 'system'];
  const orderedCategories = Object.keys(groupedNotifications).sort((a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b));
  return <div className="flex items-center gap-2" data-id="element-5587" data-mp="3k0xzyi1407ie">
      <Button size="sm" iconLeft={<Sparkles size={16} className="text-[#6F42C1]" data-id="element-5589" data-mp="3p1lql2lfemc3" />} className="bg-white text-[#6F42C1] border border-[#6F42C1] hover:bg-[#F8F5FC]" onClick={() => console.log('Ask WinePik clicked')} data-id="element-5588" data-mp="3p2dmkeixqm2w">
        <span className="hidden sm:inline" data-id="element-5590" data-mp="2we81trrvsx32">Ask WinePik</span>
      </Button>

      <div className="relative" data-id="element-5591" data-mp="31c162f9mjh0p">
        <button ref={buttonRef} onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} className="
            p-2 rounded-md text-[#475569] 
            hover:text-[#0EA5E9] hover:bg-[#F1F5F9]
            transition-all duration-200 ease-in-out
            min-h-[48px] min-w-[48px] flex items-center justify-center
            focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
            relative
          " aria-label="Notifications" aria-expanded={isNotificationsOpen} data-id="element-5592" data-mp="31ayhidzrxqny">
          <BellIcon size={20} data-id="element-5593" data-mp="2154fzi2yxbua" />
          {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#EF4444] rounded-full" data-id="element-5594" data-mp="1w4qhaqn9be8x"></span>}
        </button>

        {isNotificationsOpen && <div ref={dropdownRef} className="
              absolute right-0 top-full mt-2 w-[420px] 
              bg-white rounded-lg shadow-lg border border-[#CBD5E1]
              z-50 overflow-hidden
              animate-in fade-in slide-in-from-top-2 duration-200
            " data-id="element-5595" data-mp="1r5nbv53d760f">





            {/* Header */}
            <div className="p-4 border-b border-[#CBD5E1]" data-id="element-5596" data-mp="1dcnyp9r21nw1">
              <div className="flex items-center justify-between mb-3" data-id="element-5597" data-mp="18gqfmvoeq3fk">
                <div data-id="element-5598" data-mp="13gcjaaw64hxd">
                  <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-5599" data-mp="13fj8myu3989y">
                    Notifications
                  </h3>
                  {filteredUnreadCount > 0 && <p className="text-sm text-[#94A3B8]" data-id="element-5600" data-mp="zhd8i9nr9qy0">
                      {filteredUnreadCount} unread
                    </p>}
                </div>
                {filteredUnreadCount > 0 && <Button onClick={handleMarkAllRead} variant="ghost" size="sm" iconLeft={<Check size={16} data-id="element-5602" data-mp="2dcptiq1wxorp" />} className="text-sm font-medium" data-id="element-5601" data-mp="2s8p90lrcqr6v">
                    Mark all read
                  </Button>}
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-1 overflow-x-auto" data-id="element-5603" data-mp="24k61w9k1gkgd">
                {(Object.keys(filterLabels) as FilterCategory[]).map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`
                      px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap
                      transition-all duration-200 ease-in-out
                      focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
                      ${activeFilter === filter ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}
                    `} data-id="element-5604" data-mp="29dgx1dfhncsg">
                      {filterLabels[filter]}
                    </button>)}
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[500px] overflow-y-auto" data-id="element-5605" data-mp="122md6py3hcmr">
              {orderedCategories.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-5606" data-mp="1fy826x4bvwf6">
                  <p className="text-[#94A3B8]" data-id="element-5607" data-mp="1b06h66ncdn12">No notifications</p>
                </div> : orderedCategories.map(category => {
            const items = groupedNotifications[category];
            if (!items) return null;
            return <div key={category} className="border-b border-[#E2E8F0] last:border-b-0" data-id="element-5608" data-mp="1kz5hn1xyeccz">
                      {activeFilter === 'all' && <div className="px-4 py-2 bg-[#F8FAFC]" data-id="element-5609" data-mp="1ysmzmiazrh0w">
                          <h4 className="text-xs font-semibold text-[#475569] uppercase tracking-wide" data-id="element-5610" data-mp="1upqpzh3fiv93">
                            {categoryLabels[category as keyof typeof categoryLabels]}
                          </h4>
                        </div>}
                      {items.map(notification => {
                const isTask = notification.category === 'task';
                const isOverdue = isTask && notification.taskType === 'overdue';
                const isCompleted = isTask && notification.taskType === 'completed';
                return <div key={notification.id} onClick={() => handleNotificationClick(notification)} className={`
                              px-4 py-3 hover:bg-[#F8FAFC] cursor-pointer
                              transition-colors duration-200 ease-in-out
                              border-l-2 ${notification.unread ? 'border-l-[#0EA5E9] bg-[#F0F9FF]' : 'border-l-transparent'}
                            `} data-id="element-5611" data-mp="2na1j29akf0va">
                            <div className="flex items-start gap-2.5" data-id="element-5612" data-mp="314c2cb3sm2ty">
                              {/* Priority dot or completed checkmark for tasks */}
                              {isTask && <div className="flex-shrink-0 mt-1" data-id="element-5613" data-mp="1ulnok3ioqmoh">
                                  {isCompleted ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" data-id="element-5614" data-mp="1pncd5ocqs445" /> : <span className="block w-2.5 h-2.5 rounded-full mt-0.5" style={{
                        backgroundColor: taskPriorityDotColor[notification.taskPriority || 'Medium']
                      }} data-id="element-5615" data-mp="1fl8lhpvm5tlu" />}
                                </div>}
                              <div className="flex-1 min-w-0" data-id="element-5616" data-mp="1ph1y199c1n3m">
                                <div className="flex items-start justify-between gap-3" data-id="element-5617" data-mp="1uaeid76zb7qk">
                                  <p className={`text-sm leading-relaxed ${isOverdue ? 'text-[#EF4444] font-medium' : isCompleted ? 'text-[#64748B]' : notification.unread ? 'text-[#0F172A] font-medium' : 'text-[#475569]'}`} data-id="element-5618" data-mp="1ya6old8wh1sr">
                                    {notification.message}
                                  </p>
                                  {notification.unread && <span className="flex-shrink-0 w-2 h-2 mt-1.5 bg-[#0EA5E9] rounded-full" data-id="element-5619" data-mp="2d64o95i18o9q"></span>}
                                </div>
                                <p className={`text-xs mt-1 ${isOverdue ? 'text-[#EF4444]' : 'text-[#94A3B8]'}`} data-id="element-5620" data-mp="ivhl5a0n5c84">
                                  {notification.time}
                                </p>
                              </div>
                            </div>
                          </div>;
              })}
                    </div>;
          })}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-[#CBD5E1] bg-[#F8FAFC]" data-id="element-5621" data-mp="frti71ijaev7">
              <button onClick={() => {
            setIsNotificationsOpen(false);
            navigate('/notifications');
          }} className="
                  w-full py-2 text-sm font-medium text-[#0EA5E9]
                  hover:bg-white rounded-md
                  transition-all duration-200 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
                " data-id="element-5622" data-mp="ts4beb7bg5jj">





                View all notifications
              </button>
            </div>
          </div>}
      </div>

      <button className="
          p-2 rounded-md text-[#475569] 
          hover:text-[#0EA5E9] hover:bg-[#F1F5F9]
          transition-all duration-200 ease-in-out
          min-h-[48px] min-w-[48px] flex items-center justify-center
          focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
        " aria-label="Product tours and help" data-id="element-5623" data-mp="18itstvbdegvu">
        <HelpCircleIcon size={20} data-id="element-5624" data-mp="1n3po0oihelxe" />
      </button>
    </div>;
}